import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import * as api from '../api';

const GameContext = createContext(null);

export function GameProvider({ children }) {
  const [user, setUser] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [allInventories, setAllInventories] = useState([]);
  const [ships, setShips] = useState([]);
  const [drones, setDrones] = useState([]);
  const [stations, setStations] = useState([]);
  const [fleetLimits, setFleetLimits] = useState(null);
  const [stationEffects, setStationEffects] = useState(null);
  const [activeSector, setActiveSector] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Bootstrap: load full player state from /auth/me
  const loadGameState = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.getMe();
      setUser(data.user);
      setInventory(data.inventory);
      setShips(data.ships || []);
      setDrones(data.drones || []);
      setStations(data.stations || []);
      if (data.fleetLimits) setFleetLimits(data.fleetLimits);

      // Load sectors
      try {
        const secRes = await api.getSectors();
        setSectors(secRes.data.sectors || []);
      } catch (_) { /* not critical */ }

      // Load all inventories
      try {
        const invRes = await api.getAllInventories();
        setAllInventories(invRes.data.inventories || []);
      } catch (_) { /* not critical */ }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('not_authenticated');
      } else {
        setError('Failed to load game state');
        console.error('[Galaxy] Load error:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGameState();
  }, [loadGameState]);

  // Refresh just inventory (called after mutations)
  const refreshInventory = useCallback(async () => {
    try {
      const { data } = await api.getInventory();
      setInventory(data.inventory);
      // Also refresh all inventories
      const invRes = await api.getAllInventories();
      setAllInventories(invRes.data.inventories || []);
    } catch (err) {
      console.error('[Galaxy] Refresh inventory error:', err);
    }
  }, []);

  // Get inventory for a specific sector
  const getSectorInventory = useCallback((sectorId) => {
    return allInventories.find(inv => inv.sector_id === sectorId) || null;
  }, [allInventories]);

  // Refresh fleet
  const refreshFleet = useCallback(async () => {
    try {
      const { data } = await api.getFleet();
      setShips(data.ships || []);
      setDrones(data.drones || []);
      if (data.limits) setFleetLimits(data.limits);
    } catch (err) {
      console.error('[Galaxy] Refresh fleet error:', err);
    }
  }, []);

  // Refresh stations
  const refreshStations = useCallback(async () => {
    try {
      const { data } = await api.getStations();
      setStations(data.stations || []);
      if (data.effects) setStationEffects(data.effects);
    } catch (err) {
      console.error('[Galaxy] Refresh stations error:', err);
    }
  }, []);

  // Refresh sectors
  const refreshSectors = useCallback(async () => {
    try {
      const secRes = await api.getSectors();
      setSectors(secRes.data.sectors || []);
    } catch (err) {
      console.error('[Galaxy] Refresh sectors error:', err);
    }
  }, []);

  // Update inventory from a server response (avoids extra round-trip)
  const updateInventory = useCallback((inv) => {
    if (inv) setInventory(inv);
  }, []);

  const value = {
    user, inventory, allInventories, ships, drones, stations, fleetLimits, stationEffects,
    activeSector, setActiveSector, sectors, setSectors,
    loading, error,
    loadGameState, refreshInventory, refreshFleet, refreshStations, refreshSectors,
    updateInventory, getSectorInventory,
    setShips, setDrones, setStations, setUser,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGame must be used within GameProvider');
  return ctx;
}
